import React, { useEffect, useState } from 'react';
import { Pressable, Text, View, useWindowDimensions } from 'react-native';
import { Button, Modal, Portal, Switch } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { TextInput, toneColors } from './ui';
import { AppUser, userService } from '../services/userService';

const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,50}$/;
const MIN_PASSWORD_LENGTH = 8;

type EditCredentialsModalProps = {
  user: AppUser | null;
  /** The super administrator is editing their own account (they will be signed out). */
  isSelf: boolean;
  onDismiss: () => void;
  onSaved: (user: AppUser) => void;
};

/** Super administrator only: change the login and/or the password of any account. */
export default function EditCredentialsModal({ user, isSelf, onDismiss, onSaved }: EditCredentialsModalProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { width: windowWidth } = useWindowDimensions();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mustChange, setMustChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUsername(user?.username ?? '');
    setPassword('');
    setConfirm('');
    setShowPassword(false);
    setMustChange(!isSelf);
    setError(null);
  }, [user, isSelf]);

  if (!user) return null;

  const trimmed = username.trim();
  const renaming = trimmed !== user.username;
  const changingPassword = password.length > 0;

  const handleSave = async () => {
    if (!USERNAME_PATTERN.test(trimmed)) {
      setError("L'identifiant doit contenir 3 à 50 caractères : lettres, chiffres, points, tirets ou underscores.");
      return;
    }
    if (changingPassword && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    if (changingPassword && password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    if (!renaming && !changingPassword) {
      setError('Modifiez l\'identifiant ou saisissez un nouveau mot de passe.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await userService.updateCredentials(user.id, {
        username: renaming ? trimmed : undefined,
        newPassword: changingPassword ? password : undefined,
        mustChangePassword: changingPassword ? mustChange : undefined,
      });
      onSaved(updated);
    } catch (e: any) {
      setError(e?.response?.data?.erreur ?? 'Impossible de modifier les identifiants.');
    } finally {
      setSaving(false);
    }
  };

  const danger = toneColors(colors, 'danger');
  const warning = toneColors(colors, 'warning');

  return (
    <Portal>
      <Modal
        visible
        onDismiss={() => { if (!saving) onDismiss(); }}
        contentContainerStyle={[styles.modal, { width: Math.min(560, windowWidth - 32) }]}
      >
        <Text style={styles.title}>Identifiants de connexion</Text>
        <Text style={styles.hint}>
          {user.nom ? `${user.nom} · ` : ''}Les sessions ouvertes de ce compte seront fermées.
        </Text>

        {isSelf ? (
          <View style={[styles.banner, { backgroundColor: warning.bg }]}>
            <MaterialCommunityIcons name="logout" size={18} color={warning.fg} />
            <Text style={[styles.bannerText, { color: warning.fg }]}>
              C'est votre compte : vous serez déconnecté après l'enregistrement.
            </Text>
          </View>
        ) : null}
        {error ? (
          <View style={[styles.banner, { backgroundColor: danger.bg }]} accessibilityRole="alert">
            <MaterialCommunityIcons name="alert-circle-outline" size={18} color={danger.fg} />
            <Text style={[styles.bannerText, { color: danger.fg }]}>{error}</Text>
          </View>
        ) : null}

        <TextInput
          label="Identifiant"
          value={username}
          onChangeText={setUsername}
          autoCapitalize="none"
          autoCorrect={false}
          left={<TextInput.Icon icon="account-outline" />}
          style={styles.input}
        />
        <TextInput
          label="Nouveau mot de passe"
          value={password}
          onChangeText={setPassword}
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoComplete="new-password"
          left={<TextInput.Icon icon="lock-outline" />}
          right={
            <TextInput.Icon
              icon={showPassword ? 'eye-off-outline' : 'eye-outline'}
              onPress={() => setShowPassword((v) => !v)}
              accessibilityLabel={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            />
          }
          style={styles.input}
        />
        {changingPassword ? (
          <>
            <TextInput
              label="Confirmer le mot de passe"
              value={confirm}
              onChangeText={setConfirm}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoComplete="new-password"
              left={<TextInput.Icon icon="lock-check-outline" />}
              style={styles.input}
            />
            <Pressable
              style={styles.switchRow}
              onPress={() => setMustChange((v) => !v)}
              accessibilityRole="switch"
              accessibilityState={{ checked: mustChange }}
            >
              <View style={styles.switchText}>
                <Text style={styles.switchLabel}>Changement obligatoire à la prochaine connexion</Text>
                <Text style={styles.switchHint}>Recommandé si vous transmettez ce mot de passe à quelqu'un.</Text>
              </View>
              <Switch value={mustChange} onValueChange={setMustChange} color={colors.primary} />
            </Pressable>
          </>
        ) : (
          <Text style={styles.fieldHint}>Laissez vide pour garder le mot de passe actuel. 8 caractères minimum.</Text>
        )}

        <View style={styles.actions}>
          <Button textColor={colors.textPrimary} onPress={onDismiss} disabled={saving}>
            Annuler
          </Button>
          <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving}>
            Enregistrer
          </Button>
        </View>
      </Modal>
    </Portal>
  );
}

const useStyles = makeStyles((c) => ({
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    alignSelf: 'center',
  },
  title: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 6 },
  hint: { fontSize: 14, lineHeight: 20, color: c.textSecondary, marginBottom: 16 },
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: RADIUS.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 14,
  },
  bannerText: { flex: 1, fontSize: 14, lineHeight: 20, fontWeight: '500' },
  input: { marginBottom: 12 },
  fieldHint: { fontSize: 13, color: c.textMuted, marginTop: -4 },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  switchText: { flex: 1 },
  switchLabel: { fontSize: 14, fontWeight: '600', color: c.textPrimary },
  switchHint: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
}));
